import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import {
  MEDICATION_JSON_PREFIX,
  ROUTES,
  TIME_SLOTS,
  SCHEDULE_TYPE,
  INTERVAL_HOURS_OPTIONS,
  FOOD_TIMING_OPTIONS,
  migrateOldTimingToNew,
  extractMedicationPayload,
  buildPrescriptionSummary,
} from '@medical/shared/utils/medicationUtils';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 1000;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.25rem;
    padding-top: 0.5rem;
  }
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 1.25rem;
  width: min(960px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 20px 45px rgba(15, 23, 42, 0.18),
    0 2px 8px rgba(15, 23, 42, 0.06);

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 1rem;
    width: 100%;
  }

  @media (max-width: 480px) {
    max-height: 98vh;
    border-radius: 0.75rem;
  }
`;

const ModalHeader = styled.div`
  padding: 1.75rem 2rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem 1rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 1rem 0.875rem;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1;
  min-width: 0;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
    word-break: break-word;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }

    @media (max-width: 480px) {
      font-size: 1.125rem;
    }
  }

  span {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.4;
    word-break: break-word;

    @media (max-width: 480px) {
      font-size: 0.875rem;
    }
  }
`;

const CloseButton = styled.button`
  border: none;
  background: #f1f5f9;
  color: #1e293b;
  font-size: 1.375rem;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.85rem;
  cursor: pointer;
  line-height: 1;
  transition: background 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;
  min-width: 44px;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    width: 2.5rem;
    height: 2.5rem;
    font-size: 1.25rem;
    min-width: 40px;
    min-height: 40px;
  }
`;

const ModalBody = styled.div`
  padding: 1.75rem 2rem;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem 2rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem 1.5rem;
    gap: 0.625rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    flex-direction: column-reverse;
    gap: 0.5rem;
    
    button {
      width: 100%;
    }
  }
`;

const ActionButton = styled.button`
  border: none;
  border-radius: 0.75rem;
  padding: 0.85rem 1.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  font-size: 0.9375rem;

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: #ffffff;
          box-shadow: 0 12px 24px rgba(37, 99, 235, 0.25);

          &:hover {
            filter: brightness(0.98);
            transform: translateY(-1px);
          }

          &:active {
            transform: translateY(0) scale(0.98);
          }
        `;
      case 'outline':
        return `
          background: #ffffff;
          color: #1e40af;
          border: 1px solid rgba(37, 99, 235, 0.25);

          &:hover {
            background: #eff6ff;
          }

          &:active {
            transform: scale(0.98);
          }
        `;
      default:
        return `
          background: #f8fafc;
          color: #475569;

          &:hover {
            background: #e2e8f0;
          }

          &:active {
            transform: scale(0.98);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    min-height: 48px;
    width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  background: #ffffff;
  box-shadow: inset 0 1px 0 rgba(148, 163, 184, 0.08);

  &:last-of-type {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-radius: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1.25rem;
    border-radius: 0.75rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.3;
  }

  span {
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.4;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 1rem;

    h3 {
      font-size: 1rem;
    }

    span {
      font-size: 0.8125rem;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 0.875rem;

    h3 {
      font-size: 0.9375rem;
    }
  }
`;

const InfoBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.9rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
`;

const Grid = styled.div`
  display: grid;
  gap: 1.1rem;
  grid-template-columns: repeat(auto-fit, minmax(${({ min }) => min || '230px'}, 1fr));

  @media (max-width: 768px) {
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(${({ min }) => {
      const baseMin = parseInt(min || '230px') || 230;
      return Math.max(180, baseMin * 0.85) + 'px';
    }}, 1fr));
  }

  @media (max-width: 480px) {
    gap: 0.875rem;
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;

  @media (max-width: 480px) {
    font-size: 0.8125rem;
    gap: 0.375rem;
  }
`;

const TextInput = styled.input`
  border: 1px solid rgba(148, 163, 184, 0.6);
  border-radius: 0.75rem;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  color: #0f172a;
  outline: none;
  transition: border 0.15s ease;
  min-height: 44px;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  width: 100%;

  &:focus {
    outline: none !important;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:active {
    outline: none;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    min-height: 48px;
    border-radius: 0.625rem;
  }
`;

const SelectInput = styled.select`
  border: 1px solid rgba(148, 163, 184, 0.6);
  border-radius: 0.75rem;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  color: #0f172a;
  outline: none;
  transition: border 0.15s ease;
  min-height: 44px;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  width: 100%;
  background: #ffffff;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.9rem center;
  padding-right: 2.5rem;

  &:focus {
    outline: none !important;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:active {
    outline: none;
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    padding-right: 2.5rem;
    font-size: 0.9375rem;
    min-height: 48px;
    border-radius: 0.625rem;
  }
`;

const TextArea = styled.textarea`
  border: 1px solid rgba(148, 163, 184, 0.6);
  border-radius: 0.75rem;
  padding: 0.85rem 0.95rem;
  font-size: 0.95rem;
  color: #0f172a;
  min-height: ${({ rows }) => (rows ? `${rows * 1.1}rem` : '140px')};
  resize: vertical;
  transition: border 0.15s ease, box-shadow 0.15s ease;
  width: 100%;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.9375rem;
    border-radius: 0.625rem;
    min-height: ${({ rows }) => (rows ? `${rows * 1}rem` : '120px')};
  }
`;

const MedicationItemCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.25rem;
  padding-left: 2.75rem;
  background: #f8fafc;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &::before {
    content: '${({ index }) => index}.';
    position: absolute;
    top: 1rem;
    left: 1rem;
    font-weight: 700;
    color: #2563eb;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1.125rem;
    padding-left: 2.5rem;
    gap: 0.875rem;
    border-radius: 0.875rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    padding-left: 2.25rem;
    gap: 0.75rem;
    border-radius: 0.75rem;

    &::before {
      top: 0.875rem;
      left: 0.875rem;
      font-size: 0.9375rem;
    }
  }
`;

const RemoveButton = styled.button`
  align-self: flex-end;
  padding: 0.4rem 0.9rem;
  border-radius: 0.6rem;
  border: none;
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.78rem;
  transition: background 0.15s ease;
  min-height: 36px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    min-height: 40px;
    align-self: stretch;
    width: 100%;
  }
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(100px, 1fr));
  gap: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
    gap: 0.625rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

const SchedulePill = styled.button`
  border-radius: 999px;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ active }) => (active ? '#2563eb' : '#cbd5f5')};
  background: ${({ active }) => (active ? 'rgba(37, 99, 235, 0.1)' : '#ffffff')};
  color: ${({ active }) => (active ? '#1d4ed8' : '#475569')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 40px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    border-color: #2563eb;
    background: rgba(37, 99, 235, 0.15);
    color: #1d4ed8;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const FoodToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const FoodOption = styled.button`
  border-radius: 0.75rem;
  padding: 0.6rem 0.9rem;
  border: 1px solid ${({ active }) => (active ? '#059669' : '#cbd5e1')};
  background: ${({ active }) => (active ? 'rgba(16, 185, 129, 0.12)' : '#ffffff')};
  color: ${({ active }) => (active ? '#047857' : '#475569')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 40px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:hover {
    border-color: #059669;
    background: rgba(16, 185, 129, 0.2);
    color: #047857;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
    min-height: 44px;
    flex: 1;
    min-width: 0;
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  width: 100%;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TimeSlotGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TimeSlotLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  margin-bottom: 0.25rem;
`;

const TimeSlotCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #7c3aed;
  }

  span {
    font-size: 0.875rem;
    color: #334155;
    user-select: none;
  }
`;

const FoodTimingGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const FoodTimingOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  border-radius: 0.75rem;
  border: 1px solid ${({ active }) => (active ? '#7c3aed' : '#cbd5e1')};
  background: ${({ active }) => (active ? 'rgba(124, 58, 237, 0.12)' : '#ffffff')};
  color: ${({ active }) => (active ? '#5b21b6' : '#475569')};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  min-height: 42px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  outline: none;
  user-select: none;

  &:hover:not(:disabled) {
    border-color: ${({ active }) => (active ? '#7c3aed' : '#94a3b8')};
    background: ${({ active }) => (active ? 'rgba(124, 58, 237, 0.15)' : '#f8fafc')};
  }

  input[type="radio"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #7c3aed;
  }

  ${({ active }) => active && `
    border-width: 2px;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
  `}

  @media (max-width: 480px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.8125rem;
    min-height: 40px;
  }
`;

const HistoryPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  background: #f8fafc;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 320px;
  overflow-y: auto;
`;

const HistoryItem = styled.button`
  text-align: left;
  border: 1px solid ${({ active }) => (active ? '#2563eb' : '#e2e8f0')};
  border-radius: 0.8rem;
  background: ${({ active }) => (active ? 'rgba(37, 99, 235, 0.06)' : '#ffffff')};
  padding: 0.95rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  cursor: pointer;
  transition: border 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #2563eb;
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
  }

  h4 {
    margin: 0;
    font-size: 0.92rem;
    color: #0f172a;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: #64748b;
  }
`;

const EmptyHistory = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
  font-size: 0.9rem;
`;

const createMedicationRow = () => ({
  name: '',
  form: '',
  strength: '',
  dose: '',
  quantity: '',
  durationDays: '',
  route: 'PO', // Default to PO (Per os/by mouth)
  scheduleType: SCHEDULE_TYPE.TIME_SLOTS, // 'timeSlots' or 'interval'
  timeSlots: [], // Array of time slots when scheduleType is timeSlots
  intervalHours: 4, // When scheduleType is 'interval', e.g. every 4 hours
  firstDoseTime: '08:00', // When scheduleType is 'interval', e.g. "10:00" for 10 AM
  foodTiming: 'notMealDependent', // 'beforeFood', 'afterFood', or 'notMealDependent'
  notes: '',
});

const MedicationPrescriptionModal = ({ patient, doctor, onClose }) => {
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historySelection, setHistorySelection] = useState(null);
  const [sending, setSending] = useState(false);

  const [headerInfo, setHeaderInfo] = useState(() => ({
    organizationName: '',
    organizationAddress: '',
    organizationContact: '',
    doctorName: doctor ? `Dr. ${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() : '',
    doctorRegistration: doctor?.registrationNumber || '',
    doctorPhone: doctor?.phone || '',
  }));

  const [issuedAt, setIssuedAt] = useState(() => new Date());
  const [medications, setMedications] = useState([createMedicationRow()]);

  useEffect(() => {
    if (!patient) return;

    const loadHistory = async () => {
      try {
        setLoadingHistory(true);
        const token =
          localStorage.getItem('doctorToken') ||
          JSON.parse(localStorage.getItem('doctorUser') || '{}').token;

        const response = await axios.get('/api/messages', {
          params: {
            patientId: patient._id,
            limit: 20,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.data?.success) {
          setHistory([]);
          return;
        }

        const meds = (response.data.messages || [])
          .filter((msg) => msg.type === 'medication')
          .map((msg) => {
            const { payload, summary } = extractMedicationPayload(msg.message);
            return {
              id: msg._id,
              raw: msg,
              payload,
              summary,
              createdAt: msg.createdAt,
            };
          })
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setHistory(meds);

        if (meds.length > 0 && meds[0].payload) {
          setHistorySelection(meds[0].id);
        }
      } catch (error) {
        console.error('Error loading medication history:', error);
        toast.error('Failed to load previous prescriptions');
      } finally {
        setLoadingHistory(false);
      }
    };

    loadHistory();
  }, [patient]);

  useEffect(() => {
    if (!historySelection) return;

    const selected = history.find((entry) => entry.id === historySelection);
    if (!selected?.payload) {
      return;
    }

    const payload = selected.payload;
    setHeaderInfo((prev) => ({
      organizationName: payload.organizationName || prev.organizationName || '',
      organizationAddress: payload.organizationAddress || prev.organizationAddress || '',
      organizationContact: payload.organizationContact || prev.organizationContact || '',
      doctorName: payload.doctor?.name || prev.doctorName || '',
      doctorRegistration: payload.doctor?.registrationNumber || prev.doctorRegistration || '',
      doctorPhone: payload.doctor?.phone || prev.doctorPhone || '',
    }));

    setIssuedAt(payload.issuedAt ? new Date(payload.issuedAt) : new Date());
    if (payload.medications?.length) {
      const rows = payload.medications.map((item) => {
        const timing = migrateOldTimingToNew(item);
        return {
          name: item.name || '',
          form: item.form || '',
          strength: item.strength || '',
          dose: item.dose || '',
          quantity: item.quantity || '',
          durationDays: item.duration
            ? parseInt(String(item.duration).replace(/\D+/g, ''), 10) || ''
            : '',
          route: item.route || 'PO',
          scheduleType: timing.scheduleType || SCHEDULE_TYPE.TIME_SLOTS,
          timeSlots: Array.isArray(timing.timeSlots) ? timing.timeSlots : [],
          intervalHours: timing.intervalHours ?? 4,
          firstDoseTime: timing.firstDoseTime || '08:00',
          foodTiming: timing.foodTiming || 'notMealDependent',
          notes: item.notes || '',
        };
      });
      setMedications(rows);
    } else {
      setMedications([createMedicationRow()]);
    }
  }, [historySelection, history]);

  const updateMedication = (index, updater) => {
    setMedications((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...updater } : item)),
    );
  };

  const addMedicationRow = () => {
    setMedications((prev) => [...prev, createMedicationRow()]);
  };

  const removeMedicationRow = (index) => {
    setMedications((prev) => prev.filter((_, idx) => idx !== index));
  };

  const derivedValidUntil = useMemo(() => {
    const durations = medications
      .map((med) => parseInt(med.durationDays, 10))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (durations.length === 0) {
      return null;
    }

    const longest = Math.max(...durations);
    return addDays(new Date(issuedAt), longest);
  }, [issuedAt, medications]);

  const prescriptionPayload = useMemo(() => {
    const meds = medications
      .map((item) => ({
        name: item.name.trim(),
        form: item.form.trim(),
        strength: item.strength.trim(),
        dose: item.dose.trim(),
        quantity: item.quantity.trim(),
        duration: item.durationDays
          ? `${item.durationDays} day${Number(item.durationDays) > 1 ? 's' : ''}`
          : '',
        route: item.route || 'PO',
        scheduleType: item.scheduleType || SCHEDULE_TYPE.TIME_SLOTS,
        timeSlots: Array.isArray(item.timeSlots) ? item.timeSlots : [],
        intervalHours: item.scheduleType === SCHEDULE_TYPE.INTERVAL ? (item.intervalHours || 4) : undefined,
        firstDoseTime: item.scheduleType === SCHEDULE_TYPE.INTERVAL ? (item.firstDoseTime || '08:00') : undefined,
        foodTiming: item.foodTiming || 'notMealDependent',
        notes: item.notes.trim(),
      }))
      .filter((item) => item.name);

    if (meds.length === 0) {
      return null;
    }

    return {
      organizationName: headerInfo.organizationName.trim(),
      organizationAddress: headerInfo.organizationAddress.trim(),
      organizationContact: headerInfo.organizationContact.trim(),
      doctor: {
        name: headerInfo.doctorName.trim(),
        registrationNumber: headerInfo.doctorRegistration.trim(),
        phone: headerInfo.doctorPhone.trim(),
      },
      patient: {
        name: patient?.fullName || '',
        id: patient?.patientId || '',
      },
      issuedAt: issuedAt ? new Date(issuedAt).toISOString() : new Date().toISOString(),
      validUntil: derivedValidUntil ? derivedValidUntil.toISOString() : undefined,
      medications: meds,
      additionalInstructions: '',
      pharmacyNotes: '',
    };
  }, [headerInfo, patient, issuedAt, derivedValidUntil, medications]);

  const handleSend = async () => {
    if (!patient) {
      toast.error('No patient selected');
      return;
    }

    if (!prescriptionPayload) {
      toast.error('Add at least one medication with details');
      return;
    }

    try {
      setSending(true);
      const token =
        localStorage.getItem('doctorToken') ||
        JSON.parse(localStorage.getItem('doctorUser') || '{}').token;

      const summaryText = buildPrescriptionSummary(prescriptionPayload, {
        includeHeader: true,
        includeInstructions: true,
      });

      const messageBody = `${summaryText}\n\n${MEDICATION_JSON_PREFIX}${JSON.stringify(
        prescriptionPayload,
      )}`;

      const response = await axios.post(
        '/api/messages',
        {
          patientId: patient._id,
          subject: `Prescription • ${format(new Date(), 'MMM dd, yyyy')}`,
          message: messageBody,
          type: 'medication',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to send prescription');
      }

      toast.success('Prescription sent successfully');
      window.dispatchEvent(
        new CustomEvent('medicationSent', {
          detail: { patientId: patient._id },
        }),
      );

      onClose?.();
    } catch (error) {
      console.error('Failed to send prescription:', error);
      toast.error(error.response?.data?.message || 'Failed to send prescription');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setHistorySelection(null);
    setMedications([createMedicationRow()]);
  };

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on any child elements
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks inside the modal from closing it
    e.stopPropagation();
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalCard onClick={handleModalClick}>
        <ModalHeader>
          <HeaderTitle>
            <h2>Medication Prescription</h2>
            <span>
              {patient?.fullName} • {patient?.patientId}
            </span>
          </HeaderTitle>
          <CloseButton type="button" onClick={onClose} aria-label="Close prescription modal">
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Section>
            <SectionHeader>
              <h3>Prescription Details</h3>
              <span>
                Issued{' '}
                {issuedAt
                  ? format(new Date(issuedAt), 'MMM dd, yyyy • h:mm a')
                  : format(new Date(), 'MMM dd, yyyy')}
              </span>
            </SectionHeader>

            <Grid min="240px">
              <Field>
                Issue Date & Time
                <TextInput
                  type="datetime-local"
                  value={format(new Date(issuedAt), "yyyy-MM-dd'T'HH:mm")}
                  onChange={(e) => setIssuedAt(new Date(e.target.value))}
                />
              </Field>
              {derivedValidUntil && (
                <Field>
                  Valid Until
                  <TextInput
                    type="text"
                    value={format(derivedValidUntil, 'MMM dd, yyyy')}
                    disabled
                  />
                </Field>
              )}
            </Grid>

            <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {medications.map((medication, index) => (
                <MedicationItemCard key={index} index={index + 1}>
                  {medications.length > 1 && (
                    <RemoveButton type="button" onClick={() => removeMedicationRow(index)}>
                      Remove
                    </RemoveButton>
                  )}

                  <Grid min="220px">
                    <Field>
                      Medication / Tablet Name *
                      <TextInput
                        placeholder="e.g., Paracetamol"
                        value={medication.name}
                        onChange={(e) =>
                          updateMedication(index, { name: e.target.value })
                        }
                        required
                      />
                    </Field>
                    <Field>
                      Form
                      <TextInput
                        placeholder="Tablet / Capsule / Syrup"
                        value={medication.form}
                        onChange={(e) =>
                          updateMedication(index, { form: e.target.value })
                        }
                      />
                    </Field>
                    <Field>
                      Strength
                      <TextInput
                        placeholder="e.g., 500 mg"
                        value={medication.strength}
                        onChange={(e) =>
                          updateMedication(index, { strength: e.target.value })
                        }
                      />
                    </Field>
                    <Field>
                      Quantity
                      <TextInput
                        placeholder="e.g., 10 tablets"
                        value={medication.quantity}
                        onChange={(e) =>
                          updateMedication(index, { quantity: e.target.value })
                        }
                      />
                    </Field>
                    <Field>
                      Dose
                      <TextInput
                        placeholder="e.g., 1 tablet"
                        value={medication.dose}
                        onChange={(e) =>
                          updateMedication(index, { dose: e.target.value })
                        }
                      />
                    </Field>
                    <Field>
                      Route
                      <SelectInput
                        value={medication.route || 'PO'}
                        onChange={(e) =>
                          updateMedication(index, { route: e.target.value })
                        }
                      >
                        {ROUTES.map((route) => (
                          <option key={route} value={route}>
                            {route}
                          </option>
                        ))}
                      </SelectInput>
                    </Field>
                    <Field>
                      Duration (days)
                      <TextInput
                        type="number"
                        min="1"
                        placeholder="e.g., 5"
                        value={medication.durationDays}
                        onChange={(e) =>
                          updateMedication(index, { durationDays: e.target.value })
                        }
                        onWheel={(e) => {
                          // Prevent scroll from changing number input value
                          e.target.blur();
                        }}
                      />
                    </Field>
                  </Grid>

                  <div>
                    <Field>
                      Schedule type
                      <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal', display: 'block', marginTop: '0.25rem' }}>
                        Fixed times (morning/evening etc.) or every X hours (e.g. 10am, 2pm, 6pm, 10pm).
                      </span>
                      <TimeSlotGrid style={{ marginBottom: '1rem' }}>
                        <TimeSlotCheckbox>
                          <input
                            type="radio"
                            name={`scheduleType-${index}`}
                            checked={(medication.scheduleType || SCHEDULE_TYPE.TIME_SLOTS) === SCHEDULE_TYPE.TIME_SLOTS}
                            onChange={() => updateMedication(index, { scheduleType: SCHEDULE_TYPE.TIME_SLOTS, intervalHours: 4, firstDoseTime: '08:00' })}
                          />
                          <span>Time of day (Morning / Afternoon / Evening / Night)</span>
                        </TimeSlotCheckbox>
                        <TimeSlotCheckbox>
                          <input
                            type="radio"
                            name={`scheduleType-${index}`}
                            checked={medication.scheduleType === SCHEDULE_TYPE.INTERVAL}
                            onChange={() => updateMedication(index, { scheduleType: SCHEDULE_TYPE.INTERVAL, timeSlots: [] })}
                          />
                          <span>Every X hours (reminders at fixed intervals)</span>
                        </TimeSlotCheckbox>
                      </TimeSlotGrid>
                      {medication.scheduleType === SCHEDULE_TYPE.INTERVAL && (
                        <Grid min="180px" style={{ marginBottom: '1rem' }}>
                          <Field>
                            First dose time
                            <TextInput
                              type="time"
                              value={medication.firstDoseTime || '08:00'}
                              onChange={(e) => updateMedication(index, { firstDoseTime: e.target.value || '08:00' })}
                            />
                          </Field>
                          <Field>
                            Every (hours)
                            <SelectInput
                              value={medication.intervalHours ?? 4}
                              onChange={(e) => updateMedication(index, { intervalHours: Number(e.target.value) })}
                            >
                              {INTERVAL_HOURS_OPTIONS.map((h) => (
                                <option key={h} value={h}>Every {h} hours</option>
                              ))}
                            </SelectInput>
                          </Field>
                        </Grid>
                      )}
                    </Field>
                    <Field style={{ display: medication.scheduleType === SCHEDULE_TYPE.INTERVAL ? 'none' : undefined }}>
                      Time of Day
                      <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 'normal', display: 'block', marginTop: '0.25rem' }}>
                        Select when to take the medication. Choose multiple times OR select "Only" for a single time.
                      </span>
                      <TimeSlotGrid>
                        <TimeSlotGroup>
                          <TimeSlotLabel>Multiple Times:</TimeSlotLabel>
                          {TIME_SLOTS.MULTIPLE.map((slot) => {
                            const currentSlots = Array.isArray(medication.timeSlots) ? medication.timeSlots : [];
                            const hasOnlySlot = currentSlots.some(s => s.startsWith('only'));
                            const isChecked = currentSlots.includes(slot) && !hasOnlySlot;
                            return (
                              <TimeSlotCheckbox key={slot}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={hasOnlySlot}
                                  onChange={(e) => {
                                    const current = Array.isArray(medication.timeSlots) ? medication.timeSlots : [];
                                    let updated;
                                    if (e.target.checked) {
                                      // Remove any "only" slots and add this slot
                                      updated = current.filter(s => !s.startsWith('only'));
                                      if (!updated.includes(slot)) {
                                        updated.push(slot);
                                      }
                                    } else {
                                      updated = current.filter(s => s !== slot);
                                    }
                                    updateMedication(index, { timeSlots: updated.length > 0 ? updated : [] });
                                  }}
                                />
                                <span>{slot.charAt(0).toUpperCase() + slot.slice(1)}</span>
                              </TimeSlotCheckbox>
                            );
                          })}
                        </TimeSlotGroup>
                        <TimeSlotGroup>
                          <TimeSlotLabel>Only (Single Time):</TimeSlotLabel>
                          {TIME_SLOTS.ONLY.map((slot) => {
                            const currentSlots = Array.isArray(medication.timeSlots) ? medication.timeSlots : [];
                            const hasMultipleSlot = currentSlots.some(s => !s.startsWith('only'));
                            const isChecked = currentSlots.includes(slot) && !hasMultipleSlot;
                            return (
                              <TimeSlotCheckbox key={slot}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  disabled={hasMultipleSlot}
                                  onChange={(e) => {
                                    const current = Array.isArray(medication.timeSlots) ? medication.timeSlots : [];
                                    let updated;
                                    if (e.target.checked) {
                                      // Remove any multiple slots and set only this slot
                                      updated = [slot];
                                    } else {
                                      updated = current.filter(s => s !== slot);
                                    }
                                    updateMedication(index, { timeSlots: updated.length > 0 ? updated : [] });
                                  }}
                                />
                                <span>{slot.replace('only', 'Only ').replace(/([A-Z])/g, ' $1').trim()}</span>
                              </TimeSlotCheckbox>
                            );
                          })}
                        </TimeSlotGroup>
                      </TimeSlotGrid>
                    </Field>
                  </div>

                  <div>
                    <Field>
                      Food Timing
                      <FoodTimingGroup>
                        {FOOD_TIMING_OPTIONS.map((option) => {
                          const isActive = medication.foodTiming === option.value;
                          return (
                            <FoodTimingOption key={option.value} active={isActive}>
                              <input
                                type="radio"
                                name={`foodTiming-${index}`}
                                value={option.value}
                                checked={isActive}
                                onChange={() => updateMedication(index, { foodTiming: option.value })}
                              />
                              <span>{option.label}</span>
                            </FoodTimingOption>
                          );
                        })}
                      </FoodTimingGroup>
                    </Field>
                  </div>

                  <Field>
                    Notes / Instructions for this medication
                    <TextArea
                      rows={3}
                      placeholder="Additional instructions (e.g., take with warm water, avoid before driving, etc.)"
                      value={medication.notes}
                      onChange={(e) =>
                        updateMedication(index, { notes: e.target.value })
                      }
                    />
                  </Field>
                </MedicationItemCard>
              ))}

              <ActionButton type="button" variant="outline" onClick={addMedicationRow}>
                ➕ Add another medication
              </ActionButton>
            </div>
          </Section>

          <Section>
            <SectionHeader>
              <h3>Previous Prescriptions</h3>
              <span>Select to reuse or review</span>
            </SectionHeader>
            {loadingHistory ? (
              <LoadingSpinner text="Loading prescription history..." />
            ) : history.length === 0 ? (
              <EmptyHistory>
                No previous prescriptions found for this patient. The first prescription will appear
                here once sent.
              </EmptyHistory>
            ) : (
              <HistoryPanel>
                {history.map((entry) => (
                  <HistoryItem
                    key={entry.id}
                    type="button"
                    active={historySelection === entry.id}
                    onClick={() =>
                      setHistorySelection((prev) => (prev === entry.id ? null : entry.id))
                    }
                  >
                    <h4>
                      {format(new Date(entry.createdAt), 'MMM dd, yyyy • h:mm a')}
                      {entry.payload?.doctor?.name && ` • ${entry.payload.doctor.name}`}
                    </h4>
                    {entry.payload ? (
                      <p>
                        {entry.payload.medications?.length || 0} medication
                        {entry.payload.medications?.length === 1 ? '' : 's'} •{' '}
                        {entry.payload.medications
                          ?.map((m) => m.name)
                          .filter(Boolean)
                          .slice(0, 3)
                          .join(', ')}
                      </p>
                    ) : (
                      <p>{entry.summary?.slice(0, 140) || 'Legacy prescription'}</p>
                    )}
                  </HistoryItem>
                ))}
              </HistoryPanel>
            )}
          </Section>
        </ModalBody>

        <ModalFooter>
          <ActionButton type="button" onClick={handleReset}>
            Reset form
          </ActionButton>
          <ActionButton type="button" onClick={onClose}>
            Cancel
          </ActionButton>
          <ActionButton
            type="button"
            variant="primary"
            disabled={!prescriptionPayload || sending}
            onClick={handleSend}
          >
            {sending ? 'Sending prescription...' : 'Send Prescription'}
          </ActionButton>
        </ModalFooter>
      </ModalCard>
    </Overlay>
  );
};

export default MedicationPrescriptionModal;


